import { DataTable, Column, Badge, NameCell } from '../../../shared/components';
import { NetworkReading } from '../../../shared/services/turnoGeo/types';

export function formatReadingTime(iso: string): string {
  return new Date(iso).toLocaleString('es-CO', { timeZone: 'America/Bogota', dateStyle: 'short', timeStyle: 'medium' });
}

export function StabilityBadge({ r }: { r: NetworkReading }) {
  switch (r.stability) {
    case 'IGUAL':
      return <Badge tone="success">Igual que la anterior ✓</Badge>;
    case 'CAMBIO':
      return <Badge tone="warning">Cambió: {r.changedFields.join(', ')}</Badge>;
    case 'PRIMERA':
      return <Badge tone="info">Primera lectura</Badge>;
    default:
      return <Badge tone="neutral">Sin comparar</Badge>;
  }
}

/** Resumen honesto de estabilidad: cuántas lecturas, en cuántos días y cuántas coinciden con la anterior. */
export function StabilitySummary({ rows }: { rows: NetworkReading[] }) {
  if (rows.length === 0) return null;
  const compared = rows.filter((r) => r.stability === 'IGUAL' || r.stability === 'CAMBIO');
  const same = compared.filter((r) => r.stability === 'IGUAL').length;
  const days = new Set(rows.map((r) => r.readingDate)).size;
  return (
    <p className="text-xs text-ink-500">
      {rows.length} lectura{rows.length === 1 ? '' : 's'} en {days} día{days === 1 ? '' : 's'}.{' '}
      {compared.length > 0 ? `${same} de ${compared.length} comparaciones dieron la misma red.` : 'Aún no hay con qué comparar: guarda otra lectura otro día.'}
    </p>
  );
}

export function ReadingsTable({ rows, isLoading, showUser = false }: { rows: NetworkReading[]; isLoading: boolean; showUser?: boolean }) {
  const columns: Column<NetworkReading>[] = [
    ...(showUser ? [{ key: 'user', filterValue: (r: NetworkReading) => r.userFullName, header: 'Persona', render: (r: NetworkReading) => <NameCell name={r.userFullName} />, sortable: true, sortValue: (r: NetworkReading) => r.userFullName }] : []),
    { key: 'ts', filterValue: (r) => formatReadingTime(r.readingTimestamp).slice(0, 10), header: 'Fecha y hora', render: (r) => formatReadingTime(r.readingTimestamp), sortable: true, sortValue: (r) => r.readingTimestamp },
    { key: 'v4', header: 'IPv4', render: (r) => <span className="break-all">{r.ipv4 ?? '—'}</span> },
    { key: 'v6', header: 'Red IPv6 (prefijo)', render: (r) => <span className="break-all">{r.networkKeyV6 ?? '—'}</span> },
    { key: 'prov', filterValue: (r) => r.provider ?? '', header: 'Proveedor de internet', render: (r) => r.provider ?? '—' },
    { key: 'match', header: 'Coincide con', render: (r) => (r.matchKind === 'NINGUNA' ? <Badge tone="neutral">Ninguna</Badge> : <Badge tone="success">{r.matchKind === 'SEDE' ? `Sede: ${r.matchName}` : r.matchKind === 'OFICINA' ? 'Su oficina' : 'Su casa'}</Badge>) },
    { key: 'st', header: 'Frente a la anterior', render: (r) => <StabilityBadge r={r} /> },
  ];
  return <DataTable excel fixedKey="user" columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} emptyTitle="Sin lecturas" />;
}
